/**
 * WHERE an error happened, and WHAT raised it.
 *
 * Why this exists (2026-07-26): the admin log's location handling had two
 * holes and one live leak.
 *
 *  1. **Server and service-worker errors had no location at all.** The UI reads
 *     only `context.page`, which only the browser reporter sets — so every
 *     server error and every `[sw]` error rendered with a blank location. That
 *     is exactly the class of error the owner cannot diagnose from the browser,
 *     and it was the class with nothing to go on.
 *  2. **Three key names meant one thing** — `page`, `route`, `pathname` — set by
 *     different call sites, with the UI reading one of them.
 *  3. **The query string was stored verbatim, and two live routes carry secrets
 *     in it:** `/orders?token=…` (the magic-link order-lookup token) and
 *     `/order/[code]?session_id=…` (the Stripe Checkout session). An unhandled
 *     rejection on either page wrote a live credential into `app_errors`, which
 *     the admin UI then renders in full. `normaliseErrorPage` strips the query
 *     unconditionally — that stripping is load-bearing, not cosmetic.
 *
 * Dependency-free (like `lib/error-triage.ts`) so the browser reporter, the
 * public ingest route and Worker route handlers can all import it.
 */

/** Longest stored page path. */
const MAX_PAGE_LEN = 200;

/**
 * Where in the product an error occurred. Derived from the path — no new data
 * source, deterministic, and cheap.
 */
export type ErrorSurface =
  | "storefront"
  | "admin"
  | "florist"
  | "api"
  | "cron"
  | "webhook"
  | "worker-sw";

export const ERROR_SURFACES: readonly ErrorSurface[] = [
  "storefront",
  "admin",
  "florist",
  "api",
  "cron",
  "webhook",
  "worker-sw",
] as const;

export function isErrorSurface(v: unknown): v is ErrorSurface {
  return typeof v === "string" && (ERROR_SURFACES as readonly string[]).includes(v);
}

/**
 * HOW the error was raised.
 *
 * `source` (client | server | react-boundary | unhandled-rejection) stays
 * exactly as it is — it is a public enum on the ingest route with four
 * regression specs on it. This is an ORTHOGONAL axis that separates the 12
 * mechanisms `source`'s four values were conflating. The worst collision it
 * fixes: a service-worker `showNotification` failure (the hardest thing in this
 * app to debug) was indistinguishable from a customer's `TypeError` — both were
 * `source: "client"`.
 *
 * Deliberately mechanism-only. Cron/webhook are invocation CONTEXTS and live on
 * `surface`; mixing them in would correlate the two columns and make neither
 * filter cleanly.
 */
export type ErrorTrigger =
  | "window-error"
  | "unhandled-rejection"
  | "resource-load"
  | "react-boundary"
  | "global-error-boundary"
  | "sw-error"
  | "sw-rejection"
  | "sw-notification"
  | "sw-bridge"
  | "manual"
  | "server-catch"
  | "server-unhandled";

export const ERROR_TRIGGERS: readonly ErrorTrigger[] = [
  "window-error",
  "unhandled-rejection",
  "resource-load",
  "react-boundary",
  "global-error-boundary",
  "sw-error",
  "sw-rejection",
  "sw-notification",
  "sw-bridge",
  "manual",
  "server-catch",
  "server-unhandled",
] as const;

export function isErrorTrigger(v: unknown): v is ErrorTrigger {
  return typeof v === "string" && (ERROR_TRIGGERS as readonly string[]).includes(v);
}

/**
 * Coerce an untrusted trigger to a valid one.
 *
 * `triggerKind` arrives in the request body from `public/sw.js` (the one
 * reporter that cannot import a TS module), so it is forgeable — same status as
 * `source`. That is fine for a triage label. But unknown values are coerced to
 * `manual` rather than REJECTED: rejecting would discard real reports from a
 * stale bundle across a deploy, which is the routine state this whole pipeline
 * is built to tolerate.
 */
export function coerceErrorTrigger(v: unknown): ErrorTrigger {
  return isErrorTrigger(v) ? v : "manual";
}

/**
 * Canonical page/route for an error row. NEVER returns a query string.
 *
 * Accepts a full URL or a bare path. Dynamic segments are templated with the
 * SAME rules `buildFingerprint` uses (`lib/app-errors.ts`) so the two can never
 * drift and produce a page that groups differently from the row it belongs to.
 * Product/blog slugs are left verbatim — they are meaningful, low-cardinality
 * enough, and the admin needs to know WHICH product page broke.
 */
export function normaliseErrorPage(
  input: string | null | undefined
): string | null {
  if (!input) return null;
  let path = String(input).trim();
  if (!path) return null;

  // Full URL → pathname. A relative path with a query also has to lose it, so
  // parse against a dummy base rather than only handling absolute URLs.
  try {
    const u = new URL(path, "http://x.invalid");
    path = u.pathname;
  } catch {
    // Not URL-parseable — strip the query/hash by hand rather than giving up,
    // because giving up here is what would leak the token.
    const cut = path.search(/[?#]/);
    if (cut !== -1) path = path.slice(0, cut);
  }
  if (!path) return null;

  // Belt and braces: after every branch above, nothing resembling a query may
  // survive. A regression here re-opens the credential leak.
  path = path.split("?")[0]!.split("#")[0]!;

  path = path
    .replace(/\bFM-[A-Z0-9]+\b/gi, "FM-***")
    .replace(/\b[a-f0-9]{8,}\b/gi, "<id>")
    // Pure-numeric PATH SEGMENTS only — unlike the fingerprint's blanket
    // `\b\d+\b`, which would mangle a slug like `12-red-roses`.
    .replace(/\/\d+(?=\/|$)/g, "/<n>");

  // Trailing slash carries no meaning here and would split "/products" from
  // "/products/" into two pages.
  if (path.length > 1) path = path.replace(/\/+$/, "");
  if (!path) path = "/";

  return path.length > MAX_PAGE_LEN ? path.slice(0, MAX_PAGE_LEN) : path;
}

/**
 * Which surface a normalised path belongs to.
 *
 * `/admin/login` maps to `admin` deliberately: it is admin chrome even when
 * nobody is signed in, and the pair `surface=admin, actor=customer|bot` is a
 * genuinely useful signal (someone probing the login page).
 */
export function surfaceFromPage(
  page: string | null | undefined,
  opts?: { serviceWorker?: boolean }
): ErrorSurface | null {
  if (opts?.serviceWorker) return "worker-sw";
  if (!page) return null;
  const p = page.toLowerCase();
  if (p.startsWith("/api/cron/")) return "cron";
  if (p.startsWith("/api/webhooks/")) return "webhook";
  if (p.startsWith("/api/")) return "api";
  if (p === "/admin" || p.startsWith("/admin/")) return "admin";
  if (p === "/florist" || p.startsWith("/florist/")) return "florist";
  return "storefront";
}

/**
 * Pull a path out of the free-text `route` strings the cron + notification call
 * sites already pass (e.g. `"POST /api/cron/digest"`), so those rows get a page
 * without touching ~30 existing call sites.
 */
export function pageFromRouteLabel(
  label: string | null | undefined
): string | null {
  if (!label) return null;
  const m = /(^|\s)(\/[^\s]*)/.exec(String(label));
  return m ? normaliseErrorPage(m[2]) : null;
}
